import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Settings, Save, MapPin, Printer, ImagePlus, X, Cloud, Plus, Trash2 } from 'lucide-react';

const LOGO_WIDTH = 217;
const LOGO_HEIGHT = 100;

const emptyDestino = () => ({ id: '', nome: '', nome_m8: '', descricao: '' });

const Config = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [novoDestino, setNovoDestino] = useState(emptyDestino());
  const [config, setConfig] = useState({
    cep_viafiltros: '',
    custo_por_km: 0,
    horario_entrada: '08:00',
    horario_saida: '18:00',
    jornada_horas: 8.0,
    empresa_nome: '',
    empresa_whatsapp: '(19) 97811-5721',
    empresa_telefone: '(19) 3293-1098',
    empresa_email: 'contato@viapuroa.com.br',
    logo_impressao: null,
    m8_destinos_estoque: [],
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/config');
      const data = response.data || {};
      setConfig({
        cep_viafiltros: data.cep_viafiltros ?? '',
        custo_por_km: data.custo_por_km ?? 0,
        horario_entrada: data.horario_entrada ?? '08:00',
        horario_saida: data.horario_saida ?? '18:00',
        jornada_horas: data.jornada_horas ?? 8.0,
        latitude_viafiltros: data.latitude_viafiltros,
        longitude_viafiltros: data.longitude_viafiltros,
        raio_base_metros: data.raio_base_metros,
        wifi_base_ssid: data.wifi_base_ssid,
        horario_chegada_inicio: data.horario_chegada_inicio,
        horario_chegada_fim: data.horario_chegada_fim,
        horario_finalizacao_check: data.horario_finalizacao_check,
        auto_iniciar_apos: data.auto_iniciar_apos,
        empresa_nome: data.empresa_nome ?? '',
        empresa_whatsapp: (() => {
          if (data.empresa_whatsapp != null && data.empresa_whatsapp !== '') return data.empresa_whatsapp;
          const t = data.empresa_telefone || '';
          const idx = t.indexOf(' / Tel:');
          return idx >= 0 ? t.slice(0, idx).replace(/^WhatsApp\s*/i, '').trim() : '';
        })(),
        empresa_telefone: (() => {
          const t = data.empresa_telefone || '';
          const idx = t.indexOf(' / Tel:');
          return idx >= 0 ? t.slice(idx + 7).trim() : (t || '(19) 3293-1098');
        })(),
        empresa_email: data.empresa_email ?? 'contato@viapuroa.com.br',
        logo_impressao: data.logo_impressao ?? null,
        m8_destinos_estoque: Array.isArray(data.m8_destinos_estoque)
          ? data.m8_destinos_estoque.map((d) => ({
              id: d.id,
              nome: d.nome || '',
              nome_m8: d.nome_m8 || '',
              descricao: d.descricao || '',
            }))
          : [],
      });
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const persistirDestinos = async (lista) => {
    const res = await api.put('/m8/destinos-estoque', {
      destinos: lista.map((d) => ({
        id: Number(d.id),
        nome: (d.nome || '').trim(),
        nome_m8: (d.nome_m8 || '').trim(),
        descricao: (d.descricao || '').trim(),
      })),
    });
    const salvos = Array.isArray(res.data?.destinos) ? res.data.destinos : lista;
    setConfig((c) => ({ ...c, m8_destinos_estoque: salvos }));
    return salvos;
  };

  const adicionarDestino = async () => {
    if (saving) return;
    const idNum = parseInt(String(novoDestino.id).trim(), 10);
    const nome = (novoDestino.nome || '').trim();
    if (!idNum || idNum <= 0) {
      toast.error('Informe um ID numérico válido do destino no M8');
      return;
    }
    if (!nome) {
      toast.error('Informe o nome do destino');
      return;
    }
    if ((config.m8_destinos_estoque || []).some((d) => Number(d.id) === idNum)) {
      toast.error(`Já existe um destino com ID ${idNum}`);
      return;
    }

    try {
      setSaving(true);
      const res = await api.post('/m8/destinos-estoque/item', {
        id: idNum,
        nome,
        nome_m8: (novoDestino.nome_m8 || '').trim(),
        descricao: (novoDestino.descricao || '').trim(),
      });
      const salvos = Array.isArray(res.data?.destinos) ? res.data.destinos : [];
      setConfig((c) => ({ ...c, m8_destinos_estoque: salvos }));
      setNovoDestino(emptyDestino());
      toast.success('Destino adicionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar destino');
    } finally {
      setSaving(false);
    }
  };

  const removerDestino = async (id) => {
    if (saving) return;
    try {
      setSaving(true);
      const res = await api.delete(`/m8/destinos-estoque/${Number(id)}`);
      const salvos = Array.isArray(res.data?.destinos) ? res.data.destinos : [];
      setConfig((c) => ({ ...c, m8_destinos_estoque: salvos }));
      toast.success('Destino removido');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover destino');
    } finally {
      setSaving(false);
    }
  };

  const atualizarDestino = (id, campo, valor) => {
    setConfig({
      ...config,
      m8_destinos_estoque: (config.m8_destinos_estoque || []).map((d) =>
        Number(d.id) === Number(id) ? { ...d, [campo]: valor } : d
      ),
    });
  };

  const salvarDestinoEditado = async (id, campo, valor) => {
    const lista = (config.m8_destinos_estoque || []).map((d) =>
      Number(d.id) === Number(id) ? { ...d, [campo]: valor } : d
    );
    const destino = lista.find((d) => Number(d.id) === Number(id));
    if (!destino) return;
    const nome = (destino.nome || '').trim();
    if (!nome) {
      toast.error('Nome do destino é obrigatório');
      return;
    }
    try {
      setSaving(true);
      const salvos = await persistirDestinos(lista);
      setConfig((c) => ({ ...c, m8_destinos_estoque: salvos }));
      toast.success('Destino atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar destino');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFile = (e) => {
    const file = e.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem (PNG ou JPG).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' && dataUrl.startsWith('data:')
        ? dataUrl.split(',')[1]
        : dataUrl;
      setConfig((c) => ({ ...c, logo_impressao: base64 || null }));
      toast.success('Logo selecionado. Salve as configurações para aplicar.');
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeLogo = () => {
    setConfig((c) => ({ ...c, logo_impressao: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Destinos M8 persistem via PUT /m8/destinos-estoque (add/remove imediato).
      // Não enviar lista stale no PUT /config — evita reverter destinos em outra aba/sessão.
      const { m8_destinos_estoque: _destinos, ...configSemDestinos } = config;
      const res = await api.put('/config', configSemDestinos);
      if (res.data) {
        const { m8_destinos_estoque: _destinosResp, ...restoResposta } = res.data;
        setConfig((c) => ({
          ...c,
          ...restoResposta,
        }));
      }
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="config-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Configurações</h1>
            <p className="text-slate-600">Configure os parâmetros do sistema</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Via Filtros */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-600" />
            Localização Via Filtros
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              CEP da Via Filtros
            </label>
            <input
              type="text"
              value={config.cep_viafiltros}
              onChange={(e) => setConfig({ ...config, cep_viafiltros: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              placeholder="00000-000"
              data-testid="cep-viafiltros-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Usado como ponto de referência para cálculo de distâncias e para liberar o e-mail de reposição na base.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={config.latitude_viafiltros ?? ''}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      latitude_viafiltros: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="-22.85"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={config.longitude_viafiltros ?? ''}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      longitude_viafiltros: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                  placeholder="-47.04"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Raio (metros)</label>
                <input
                  type="number"
                  step="1"
                  value={config.raio_base_metros ?? 150}
                  onChange={(e) =>
                    setConfig({
                      ...config,
                      raio_base_metros: e.target.value === '' ? 150 : Number(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all text-sm"
                />
              </div>
            </div>
            {config.latitude_viafiltros != null && config.longitude_viafiltros != null ? (
              <p className="text-xs text-emerald-700 mt-2">
                Coordenadas OK: {Number(config.latitude_viafiltros).toFixed(5)},{' '}
                {Number(config.longitude_viafiltros).toFixed(5)}
                {config.raio_base_metros != null ? ` · raio ${config.raio_base_metros} m` : ''}
              </p>
            ) : (
              <p className="text-xs text-amber-700 mt-2 font-medium">
                Coordenadas da base ausentes — salve o CEP (geocodifica automaticamente) ou preencha lat/lng manualmente. Sem isso o app nunca detecta &quot;na base&quot; e o e-mail de pedido não sai.
              </p>
            )}
          </div>
        </div>

        {/* Dados no relatório impresso */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Printer className="w-5 h-5 text-emerald-600" />
            Dados no relatório impresso
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Contatos aparecem no cabeçalho do relatório impresso (impressora térmica no app mobile). O nome da empresa não é impresso em texto — use o logo.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">WhatsApp</label>
              <input
                type="text"
                value={config.empresa_whatsapp ?? ''}
                onChange={(e) => setConfig({ ...config, empresa_whatsapp: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                placeholder="(19) 97811-5721"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tel. fixo</label>
              <input
                type="text"
                value={config.empresa_telefone ?? ''}
                onChange={(e) => setConfig({ ...config, empresa_telefone: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                placeholder="(19) 3293-1098"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">E-mail</label>
              <input
                type="email"
                value={config.empresa_email ?? ''}
                onChange={(e) => setConfig({ ...config, empresa_email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                placeholder="contato@viapuroa.com.br"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Logo no cabeçalho (recomendado 217×100 px, PNG ou JPG)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleLogoFile}
              className="hidden"
            />
            {config.logo_impressao ? (
              <div className="flex items-start gap-4 flex-wrap">
                <div
                  className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50"
                  style={{ width: LOGO_WIDTH, height: LOGO_HEIGHT }}
                >
                  <img
                    src={`data:image/png;base64,${config.logo_impressao}`}
                    alt="Logo"
                    className="w-full h-full object-contain"
                    style={{ width: LOGO_WIDTH, height: LOGO_HEIGHT }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <ImagePlus className="w-4 h-4" />
                    Trocar logo
                  </button>
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Remover logo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                Escolher logo da empresa
              </button>
            )}
          </div>
        </div>

        {/* Custos */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Custos</h2>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Custo por KM (R$)
            </label>
            <input
              type="number"
              step="0.01"
              value={config.custo_por_km}
              onChange={(e) => setConfig({ ...config, custo_por_km: parseFloat(e.target.value) || 0 })}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
              placeholder="0.00"
              data-testid="custo-km-input"
            />
          </div>
        </div>

        {/* Jornada de Trabalho */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Jornada de Trabalho</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Horário de Entrada
              </label>
              <input
                type="time"
                value={config.horario_entrada}
                onChange={(e) => setConfig({ ...config, horario_entrada: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                data-testid="horario-entrada-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Horário de Saída
              </label>
              <input
                type="time"
                value={config.horario_saida}
                onChange={(e) => setConfig({ ...config, horario_saida: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                data-testid="horario-saida-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Jornada (horas)
              </label>
              <input
                type="number"
                step="0.5"
                value={config.jornada_horas}
                onChange={(e) => setConfig({ ...config, jornada_horas: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
                data-testid="jornada-horas-input"
              />
            </div>
          </div>
          
          <p className="text-xs text-slate-500 mt-4">
            Horas extras serão calculadas automaticamente quando a jornada ultrapassar o limite configurado
          </p>
        </div>

        {/* Destinos de estoque M8 */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" />
            Destinos de estoque M8
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            IDs de local de estoque do ERP. Use-os ao vincular o técnico em Usuários.
            O campo <strong>Nome M8</strong> deve bater com o rótulo do relatório no ERP (se vazio, é gerado automaticamente).
          </p>

          <div className="space-y-3 mb-4">
            {(config.m8_destinos_estoque || []).length === 0 && (
              <p className="text-sm text-slate-400">Nenhum destino cadastrado.</p>
            )}
            {(config.m8_destinos_estoque || []).map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start border border-slate-100 rounded-xl p-3"
              >
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">ID</label>
                  <input
                    type="number"
                    value={d.id}
                    disabled
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs text-slate-500 mb-1">Nome (exibição)</label>
                  <input
                    type="text"
                    value={d.nome}
                    onChange={(e) => atualizarDestino(d.id, 'nome', e.target.value)}
                    onBlur={(e) => salvarDestinoEditado(d.id, 'nome', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  />
                </div>
                <div className="md:col-span-5">
                  <label className="block text-xs text-slate-500 mb-1">Nome M8 (relatório)</label>
                  <input
                    type="text"
                    value={d.nome_m8 || ''}
                    onChange={(e) => atualizarDestino(d.id, 'nome_m8', e.target.value)}
                    onBlur={(e) => salvarDestinoEditado(d.id, 'nome_m8', e.target.value)}
                    placeholder={`${d.id} - ESTOQUE ${(d.nome || '').toUpperCase()}`}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
                  />
                </div>
                <div className="md:col-span-2 flex md:justify-end pt-5">
                  <button
                    type="button"
                    onClick={() => removerDestino(d.id)}
                    disabled={saving}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                    title="Remover"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Adicionar destino</p>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
              <div className="md:col-span-2">
                <label className="block text-xs text-slate-500 mb-1">ID M8</label>
                <input
                  type="number"
                  value={novoDestino.id}
                  onChange={(e) => setNovoDestino({ ...novoDestino, id: e.target.value })}
                  placeholder="ex: 14"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-xs text-slate-500 mb-1">Nome</label>
                <input
                  type="text"
                  value={novoDestino.nome}
                  onChange={(e) => setNovoDestino({ ...novoDestino, nome: e.target.value })}
                  placeholder="ex: João"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                />
              </div>
              <div className="md:col-span-5">
                <label className="block text-xs text-slate-500 mb-1">Nome M8 (opcional)</label>
                <input
                  type="text"
                  value={novoDestino.nome_m8}
                  onChange={(e) => setNovoDestino({ ...novoDestino, nome_m8: e.target.value })}
                  placeholder="14 - ESTOQUE JOÃO"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="button"
                  onClick={adicionarDestino}
                  disabled={saving}
                  className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-medium text-sm hover:bg-blue-100 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-3 rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="save-config-btn"
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-5 h-5" />
                Salvar Configurações
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Config;
